import { FormEvent, useEffect, useState } from "react";
import { Check, ShieldCheck, UserPlus, Users, X } from "lucide-react";
import { createUser, listUsers } from "../api";
import type { AuthUser, UserCreateInput } from "../types";

const roles = [
  { value: "superadmin", label: "Superadministrador", scope: "Control total, usuarios e integraciones" },
  { value: "fleet_admin", label: "Administrador de flotilla", scope: "Unidades, rutas y reportes" },
  { value: "operator", label: "Operador C5", scope: "Monitoreo, video y alertas" },
  { value: "dispatcher", label: "Despachador", scope: "Viajes, rutas y asignaciones" },
  { value: "maintenance", label: "Mantenimiento", scope: "Diagnóstico, sensores y órdenes" },
  { value: "auditor", label: "Auditor", scope: "Consulta de históricos y exportaciones" },
  { value: "client", label: "Cliente", scope: "Consulta de unidades autorizadas" },
];

const initialForm: UserCreateInput = {
  full_name: "",
  email: "",
  role: "operator",
  password: "",
};

export function UserAdminPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [form, setForm] = useState<UserCreateInput>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    listUsers().then(setUsers).catch((reason) => setError(reason.message));
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    setCreated("");
    try {
      const user = await createUser(form);
      setUsers((current) => [...current, user].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      setCreated(`${user.full_name} fue dado de alta`);
      setForm(initialForm);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No fue posible crear el usuario");
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-admin-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">CONTROL DE ACCESO · RBAC</span>
            <h2 id="user-admin-title">Usuarios y permisos</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar administración">
            <X size={18} />
          </button>
        </header>

        <section className="admin-panel__summary">
          <span><Users size={17} /><b>{users.length}</b> usuarios</span>
          <span><ShieldCheck size={17} /><b>7</b> perfiles</span>
          <span><Check size={17} /> auditoría activa</span>
        </section>

        <div className="admin-panel__body">
          <section>
            <div className="admin-section-title">
              <span className="eyebrow">DIRECTORIO ACTIVO</span>
              <b>{users.filter((user) => user.active !== false).length} habilitados</b>
            </div>
            <div className="user-directory">
              {users.map((user) => {
                const role = roles.find((item) => item.value === user.role);
                return (
                  <article key={user.id}>
                    <span>{user.full_name.slice(0, 2).toUpperCase()}</span>
                    <div>
                      <strong>{user.full_name}</strong>
                      <small>{user.email}</small>
                    </div>
                    <em>{role?.label || user.role}</em>
                    <i className={`status-dot ${user.active === false ? "" : "is-online"}`} />
                  </article>
                );
              })}
            </div>
          </section>

          <section>
            <div className="admin-section-title">
              <span className="eyebrow">NUEVA IDENTIDAD</span>
              <b>Alta auditada</b>
            </div>
            <form className="user-create-form" onSubmit={submit}>
              <label>
                NOMBRE COMPLETO
                <input
                  required
                  minLength={2}
                  value={form.full_name}
                  onChange={(event) => setForm({ ...form, full_name: event.target.value })}
                />
              </label>
              <label>
                CORREO INSTITUCIONAL
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                />
              </label>
              <label>
                PERFIL DE ACCESO
                <select
                  value={form.role}
                  onChange={(event) => setForm({ ...form, role: event.target.value })}
                >
                  {roles.map((role) => <option value={role.value} key={role.value}>{role.label}</option>)}
                </select>
                <small>{roles.find((role) => role.value === form.role)?.scope}</small>
              </label>
              <label>
                CONTRASEÑA TEMPORAL
                <input
                  required
                  type="password"
                  minLength={12}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  placeholder="Mínimo 12 caracteres"
                />
              </label>
              {error && <p className="admin-form-message is-error">{error}</p>}
              {created && <p className="admin-form-message is-success">{created}</p>}
              <button className="primary-button" disabled={loading}>
                <UserPlus size={15} />
                {loading ? "REGISTRANDO..." : "CREAR USUARIO"}
              </button>
            </form>
            <div className="role-matrix">
              {roles.map((role) => (
                <div className={form.role === role.value ? "is-selected" : ""} key={role.value}>
                  <strong>{role.label}</strong>
                  <small>{role.scope}</small>
                </div>
              ))}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
